import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PublicPhotoUpload } from '@/components/PhotoGallery';
import { CheckCircle, Briefcase } from 'lucide-react';
import { formatPreferredWindow } from '@/lib/formatters';

const API = process.env.REACT_APP_BACKEND_URL;
const PARENT_COMPANY_NAME = 'Greater Miami Valley Property Services, LLC';

function smsConsentText(dbaName) {
  return `By checking this box, I agree to receive customer care and service-related text messages from ${PARENT_COMPANY_NAME} and ${dbaName}. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out or HELP for help. Consent is not required to obtain services.`;
}

export default function PublicLeadForm() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [dba, setDba] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]);

  useEffect(() => {
    const fetchDBA = async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/dba/${slug}`);
        if (data.canonical_slug && data.canonical_slug !== slug) {
          navigate(`/intake/${data.canonical_slug}`, { replace: true });
        }
        setDba(data);
      } catch {
        setError('Business not found');
      } finally {
        setLoading(false);
      }
    };
    fetchDBA();
  }, [navigate, slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const f = new FormData(e.target);
    try {
      const resp = await axios.post(`${API}/api/public/intake/${slug}`, {
        name: f.get('name'),
        email: f.get('email'),
        phone: f.get('phone'),
        address: f.get('address'),
        service_needed: f.get('service_needed'),
        message: f.get('message'),
        preferred_date: f.get('preferred_date') || '',
        preferred_time: f.get('preferred_time') || '',
        preferred_time_frame: f.get('preferred_time_frame') || '',
        sms_consent: f.get('sms_consent') === 'on',
        sms_consent_text: f.get('sms_consent_text') || '',
        website: f.get('website') || '',
      });
      const leadId = resp.data.lead_id;
      // Upload photos
      if (photoFiles.length > 0 && leadId) {
        for (const file of photoFiles) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            await axios.post(`${API}/api/photos/upload/public?entity_type=lead&entity_id=${leadId}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch {
            setMessage('Your request was submitted, but one or more photos could not be uploaded.');
          }
        }
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="animate-pulse w-full max-w-md">
          <div className="h-96 bg-zinc-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error && !dba) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-lg font-semibold text-zinc-900">{error}</p>
          <p className="text-sm text-zinc-500 mt-1">The business you&apos;re looking for doesn&apos;t exist or is inactive.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-zinc-900 font-heading" data-testid="intake-success">Thank You!</h2>
          <p className="text-sm text-zinc-500 mt-2">We&apos;ve received your request and will be in touch soon.</p>
        </div>
      </div>
    );
  }

  const consentCopy = smsConsentText(dba.name);

  return (
    <div
      className="min-h-screen bg-zinc-100 flex items-center justify-center p-4"
      style={{
        backgroundImage: `url(https://images.unsplash.com/photo-1694702730868-a34da58c1427?w=1920&q=40)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-zinc-950/60" />
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-lg border border-zinc-200 shadow-xl p-8 sm:p-10">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-md mb-3" style={{ backgroundColor: dba.color || '#09090b' }}>
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-zinc-900 font-heading" data-testid="intake-form-title">
              {dba.name}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">{dba.tagline || 'Request a free quote'}</p>
            {dba.business_hours && <p className="text-xs text-zinc-400 mt-0.5">{dba.business_hours}</p>}
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 p-2 rounded mb-4">{error}</p>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input name="website" tabIndex={-1} autoComplete="off" className="hidden" data-testid="intake-website-honeypot" />
            <div>
              <Label>Your Name *</Label>
              <Input name="name" required data-testid="intake-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input name="email" type="email" data-testid="intake-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input name="phone" data-testid="intake-phone" />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input name="address" data-testid="intake-address" />
            </div>
            {dba.service_categories?.length > 0 && (
              <div>
                <Label>Service Needed</Label>
                <select name="service_needed" className="w-full h-9 rounded-md border border-zinc-200 bg-white px-3 text-sm" data-testid="intake-service">
                  <option value="">Select a service...</option>
                  {dba.service_categories.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Preferred Date</Label>
                <Input name="preferred_date" type="date" data-testid="intake-preferred-date" />
              </div>
              <div>
                <Label>Preferred Time Frame</Label>
                <select name="preferred_time_frame" className="w-full h-9 rounded-md border border-zinc-200 bg-white px-3 text-sm" data-testid="intake-preferred-time-frame">
                  <option value="">No preference</option>
                  <option value="morning_8_12">{formatPreferredWindow('morning_8_12')}</option>
                  <option value="midday_10_2">{formatPreferredWindow('midday_10_2')}</option>
                  <option value="afternoon_12_4">{formatPreferredWindow('afternoon_12_4')}</option>
                  <option value="evening_4_7">{formatPreferredWindow('evening_4_7')}</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Specific Time (Optional)</Label>
              <Input name="preferred_time" type="time" data-testid="intake-preferred-time" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea name="message" rows={3} placeholder="Tell us about what you need..." data-testid="intake-message" />
            </div>
            <PublicPhotoUpload onPhotosChange={setPhotoFiles} maxPhotos={5} />
            <label className="flex items-start gap-2 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600" data-testid="intake-sms-consent-label">
              <input type="checkbox" name="sms_consent" className="mt-0.5" data-testid="intake-sms-consent" />
              <span>
                {consentCopy}
              </span>
              <input type="hidden" name="sms_consent_text" value={consentCopy} />
            </label>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="intake-submit">
              {submitting ? 'Sending...' : 'Get Quote'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
